import React from 'react'
// libs
import styled from 'styled-components'
// components
import { VerticalMenu } from 'components/UI'
// views
import {
  AddIcon,
  Content,
  LinkIcon,
  RemoveIcon,
  ActionsIcon,
  CollapseIcon,
  MeasuresIcon,
  CenteredMenuItem,
  NodeTitleWrapper,
  LinkIconWithCounter
} from 'themes/views/objectivesList'
// theme
import colors from 'themes/colors'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { SelectedPlanItemsContext } from 'containers/SubPlan/layers/BL/SelectedItems'
// utils
import { isEmpty } from 'lodash'
import { find, get, pipe } from 'lodash/fp'

const CoverContainer = styled('div')`
  align-items: center;
  background-color: rgb(255, 255, 255);
  display: flex;
  height: 46px;
  justify-content: center;
  opacity: 0.9;
  pointer-events: none;
  position: absolute;
  width: calc(100% + 1px);
  z-index: 9999999;

  > span {
    position: absolute;
    color: ${colors.black};
    font-size: 15px;
    font-weight: 500;
  }
`

const NodeContent = ({
  node,
  isKPI,
  isRoot,
  isAction,
  nodeTitle,
  background,
  hasChildren,
  isObjective,
  handleCollapseClick
}) => {
  const { selectPlanItem, selectedPlanItems } = React.useContext(SelectedPlanItemsContext)
  const { enableLinkingMode } = React.useContext(LinkingModeContext)

  const { planAddItemsFromParentPlan, planLinkObjects } = node.parentPlan.currentPermissions

  // constants
  const selectedNode: any = pipe(
    get('parentPlan.planItems'),
    find(({ planObjective, planAction, planKPI }) =>
      [planObjective, planAction, planKPI].some((item) => item?.id === node.entityId)
    )
  )(node)

  const linkingItems = [...node.linkingItems.destinationLinkingItems, ...node.linkingItems.sourceLinkingItems]
  const isNodeHaveLinkedItems = !isEmpty(linkingItems)
  const isSelected = selectedPlanItems.some(({ id }) => id === node.id)

  const shouldShowMenu =
    ((planAddItemsFromParentPlan || planLinkObjects || node.isSubPlanCreation) && !node.isSubPlan) ||
    (node.isSubPlan && planLinkObjects)

  const shouldShowAddButton = (planAddItemsFromParentPlan || node.isSubPlanCreation) && !isSelected && !node.isSubPlan
  const shouldShowRemoveButton = (planAddItemsFromParentPlan || node.isSubPlanCreation) && isSelected && !node.isSubPlan

  // methods
  const onAddButtonClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    node.handleAddClick()
    selectPlanItem({ id: node.id })
  }

  const onRemoveButtonClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (selectedNode) {
      node.handleRemoveClick(selectedNode.id)
    } else {
      node.handleRemoveClick()
    }
  }

  const onLinkButtonClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    enableLinkingMode(node.entityId, node.planId, node.title, node.entityType)
  }

  React.useEffect(() => {
    if (!selectedNode) {
      return
    }
    selectPlanItem({ id: node.id })
  }, [selectedNode])

  return (
    <Content>
      {(isAction || isKPI) && (
        <>
          {isAction ? <ActionsIcon /> : <MeasuresIcon />}
          <CollapseIcon
            $isKPI={isKPI}
            color={'inherit'}
            $isAction={isAction}
            $hidden={!hasChildren}
            $isExpanded={node.expanded}
            onClick={handleCollapseClick}
          />
        </>
      )}

      {shouldShowMenu ? (
        <VerticalMenu trigger={<NodeTitleWrapper $isObjective={isObjective}>{nodeTitle}</NodeTitleWrapper>}>
          {shouldShowAddButton && (
            <CenteredMenuItem onClick={onAddButtonClick}>
              <AddIcon />
              Add to sub plan
            </CenteredMenuItem>
          )}

          {shouldShowRemoveButton && (
            <CenteredMenuItem onClick={onRemoveButtonClick}>
              <RemoveIcon />
              Remove from sub plan
            </CenteredMenuItem>
          )}

          {!node.isSubPlanCreation && planLinkObjects && (
            <CenteredMenuItem onClick={onLinkButtonClick}>
              <LinkIcon $isObjectivesModal /> Link
            </CenteredMenuItem>
          )}
        </VerticalMenu>
      ) : (
        <NodeTitleWrapper $isObjective={isObjective}>{nodeTitle}</NodeTitleWrapper>
      )}

      {isSelected && !node.isSubPlan && (
        <CoverContainer>
          <span>Selected</span>
        </CoverContainer>
      )}

      {isNodeHaveLinkedItems && !node.isSubPlanCreation && (
        <LinkIconWithCounter $background={background} $isRootObjective={isRoot && isObjective && !isSelected}>
          <div>{linkingItems.length}</div>
          <LinkIcon $isLinked $background={background} $isRootObjective={isRoot && isObjective && !isSelected} />
        </LinkIconWithCounter>
      )}
    </Content>
  )
}

export default NodeContent
